import { ChangeEvent, FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@pp/core/src/components/icons/icon';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { CurrencyFormatter } from '@pp/core/src/formatters/CurrencyFormatter';
import { GtagLogger } from '@pp/core/src/logger/GtagLogger';
import { useHideCurrency } from '@pp/core/src/store/compositeSelectors/useHideCurrency';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useIsLargerThanWide } from '@pp/core/src/styles/hooks/media';
import { roundNumber } from '@pp/core/src/utils/number';

import { Bets } from '../../config/bets';
import { useGameDispatch } from '../../store/GameStore';
import { useBetStepperSlice } from '../../store/slices/betStepper/selectors';
import { betStepperActions } from '../../store/slices/betStepper/slice';
import { highflyerGameStateActions } from '../../store/slices/highflyerGame/slice';
import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';
import { useBetsInputToolTip } from '../gameGrid/hooks/useBetsInputToolTip';
import { useBetStepperDisabled } from '../gameGrid/hooks/useBetStepperDisabled';
import { useCurrencySymbol } from '../gameGrid/hooks/useCurrencySymbol';
import { useIsBetConfirmedConnected } from '../gameGrid/hooks/useIsBetConfirmedConnected';
import { useValidBetInputValue } from '../gameGrid/hooks/useValidBetInputValue';

import { BetStepper } from './BetStepper';
import { useShowCollapsedMode } from './useShowCollapsedMode';
import { formatNumberToString, getTextWidth } from './utils';

import styles from './betStepperConnected.module.css';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';

export interface BetStepperConnectedProps {
  betCode: Bets;
}
export const BetStepperConnected: FC<BetStepperConnectedProps> = ({ betCode }): ReactElement => {
  const dispatch = useGameDispatch();
  const { betInputValue, betInputAmount } = useValidBetInputValue(betCode);
  const { betMaxLimitReached, balanceInSufficientReached } = useBetsInputToolTip(betCode);
  const balance = useGameStateSlice.getDeductibleBalance();
  const currency = useGameStateSlice.getCurrency() ?? '';
  const currentLanguage = useSettingsSlice.currentLanguage();
  const currencySymbol = useCurrencySymbol();
  const hideCurrency = useHideCurrency();
  const betLimits = useBetSlice.betLimits() ?? {};
  const betMaxLimit = betLimits[betCode].max;
  const betMinLimit = betLimits[betCode].min;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const defaultToolTipText = 'EDIT';
  const [tooltipText, setTooltipText] = useState(defaultToolTipText);
  const defaultToolTipAtts = [CurrencyFormatter.format(betMaxLimit, currentLanguage, {
    currency: currency,
    hideCurrency,
    avoidTrailingZero: true,
  })];
  const [tooltipAttrs, setTooltipAttrs] = useState(defaultToolTipAtts);
  const totalBet = useBetSlice.totalBet();
  const [disableDecrement, setDisableDecrement] = useState<boolean>(true);
  const [disableIncrement, setDisableIncrement] = useState<boolean>(true);
  const inputLabel = 'BETLABEL';
  const isDesktop = useIsLargerThanWide();
  const remainingBalance = useHighflyerStateSlice.balance();
  const isBetConfirmed = useIsBetConfirmedConnected(betCode);
  const disabled = useBetStepperDisabled(betCode);
  const lastBet = useBetStepperSlice.getLastBetInputValue(betCode);
  const audioTrigger = useAudioTrigger();
  const showCollapsedMode = useShowCollapsedMode(betCode);
  const onFocusTimerId = useRef<number>();
  const [customInputFontSize, setCustomInputFontSize] = useState(0);
  const [forceTollTip, setForceTollTip] = useState(false);
  const quickBetClick = useBetStepperSlice.quickBetClick();
  const isFirstMount = useRef(true);
  const playerConfig = useConfigSlice.playerConfig();

  const prefixComponent = useMemo(() => {
    return <Icon icon="ChevronLeft" color={'rgba(255, 255, 255, 1)'} />;
  }, []);
  const postFixComponent = useMemo(() => {
    return <Icon icon="ChevronRight" color={'rgba(255, 255, 255, 1)'} />;
  }, []);

  const removeToaster = useCallback(() => {
    if (playerConfig?.jurisdiction === 'NL') dispatch(toastActions.removeNLToast()); // TODO toaster message is = TOAST_BET_ABOVE_MINIMUM
  }, [dispatch, playerConfig?.jurisdiction]);

  const onFocus = useCallback(() => {
    if (disabled) return;
    dispatch(
      betStepperActions.setBetInputValue({
        betId: betCode,
        value: formatNumberToString(betInputAmount),
        isBetFinalized: false,
      })
    );
    setIsEditing(true);
    if (!isDesktop) {
      dispatch(highflyerGameStateActions.setToggleKeypad({ keypad: true, betCode: betCode }));
    } else {
      window.clearTimeout(onFocusTimerId.current);
      onFocusTimerId.current = window.setTimeout(() => {
        inputRef?.current?.select();
      }, 0);
    }
    setTooltipAttrs([CurrencyFormatter.format(betMinLimit, currentLanguage, {
      currency: currency,
      hideCurrency,
      avoidTrailingZero: true,
    }),
    CurrencyFormatter.format(betMaxLimit, currentLanguage, {
      currency: currency,
      hideCurrency,
      avoidTrailingZero: true,
    })]);
    setTooltipText("HIGHFLYER_BET_LIMIT_TOOLTIP");

    try {
      GtagLogger.logUniqueEvent('bet-input-focus', {
        betSpot: `Betcode ${betCode}`,
        value: formatNumberToString(betInputAmount),
      });
    } catch (e) {
      //
    }
  }, [
    isDesktop,
    currency,
    hideCurrency,
    betMinLimit,
    betMaxLimit,
    betCode,
    dispatch,
    disabled,
    currentLanguage,
    betInputAmount,
  ]);

  const onBlur = useCallback(() => {
    setIsEditing(false);
    setTooltipAttrs(defaultToolTipAtts);
    setTooltipText(defaultToolTipText);
    inputRef.current?.blur();
    dispatch(
      betStepperActions.setBetInputValue({
        betId: betCode,
        value: roundNumber(betInputAmount, 2).toFixed(2),
        isBetFinalized: true,
      })
    );
    removeToaster();
  }, [betInputAmount, dispatch, betCode]);

  const onEscape = useCallback(() => {
    const isBalanceInSufficient = betInputAmount > balance;
    const finalBetInput = isBalanceInSufficient ? balance : lastBet;
    onBlur();
    dispatch(
      betStepperActions.setBetInputValue({
        betId: betCode,
        value: finalBetInput?.toString() ?? betMinLimit.toString(),
        isBetFinalized: true,
      })
    );
  }, [betInputAmount, balance, betMinLimit, dispatch, lastBet, betCode, onBlur]);

  const onKeyPressHandler = useCallback(
    (event: any) => {
      if (!inputRef.current) return;
      switch (event.key) {
        case 'Enter':
          inputRef.current?.blur();
          break;
        case 'Escape':
          onEscape();
          break;
      }
    },
    [onEscape]
  );

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      betStepperActions.setBetInputValue({
        betId: betCode,
        value: event.target.value,
      })
    );
  };

  const getOnStepperValue = useCallback(
    (value: number) => {
      const isBetMaxLimitExceed = betInputAmount + value > betMaxLimit;
      const isBalanceInSufficient = betInputAmount + value > remainingBalance;
      if (isBetMaxLimitExceed) return betMaxLimit;
      if (isBalanceInSufficient) return remainingBalance;
      return roundNumber(betInputAmount + value, 2);
    },
    [betInputAmount, betMaxLimit, remainingBalance]
  );

  const onStepperIncrement = useCallback(() => {
    audioTrigger.play({ name: 'menuClick' });
    const incrementalValue = betInputAmount < 1 ? 0.1 : betMinLimit;
    dispatch(
      betStepperActions.setBetInputValue({
        betId: betCode,
        value: getOnStepperValue(incrementalValue).toFixed(2),
        isBetFinalized: true,
      })
    );
    removeToaster();
  }, [betInputAmount, betMinLimit, audioTrigger, getOnStepperValue, betCode, dispatch]);

  const onStepperDecrement = useCallback(() => {
    audioTrigger.play({ name: 'menuClick' });
    const decrementalValue = betInputAmount <= 1 ? 0.1 : betMinLimit;
    const isBetMinLimitExceed = betInputAmount - decrementalValue < betMinLimit;
    const finalBetInput = isBetMinLimitExceed ? betMinLimit : roundNumber(betInputAmount - decrementalValue, 2);
    dispatch(
      betStepperActions.setBetInputValue({
        betId: betCode,
        value: finalBetInput.toFixed(2),
        isBetFinalized: true,
      })
    );
    removeToaster();
  }, [betInputAmount, betMinLimit, audioTrigger, betCode, dispatch]);

  useEffect(() => {
    const childEl = inputRef.current;
    if (inputRef) {
      if (childEl) {
        childEl.addEventListener('focus', onFocus);
        childEl.addEventListener('blur', onBlur);
        childEl.addEventListener('keyup', onKeyPressHandler);
      }
    }

    return () => {
      childEl?.removeEventListener('keyup', onKeyPressHandler);
      childEl?.removeEventListener('blur', onBlur);
      childEl?.removeEventListener('focus', onFocus);
    };
  }, [inputRef, onBlur, onFocus, onKeyPressHandler]);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    const betInputValue = Math.max(remainingBalance, betMinLimit);
    // Dynamic changing of bet Input value with the remaining balance
    if (!isBetConfirmed && betInputAmount > remainingBalance) {
      dispatch(
        betStepperActions.setBetInputValue({
          betId: betCode,
          value: betInputValue.toFixed(2),
          isBetFinalized: true,
        })
      );
      dispatch(betStepperActions.resetBetStack(betCode));
    }

    setDisableDecrement(betInputAmount <= betMinLimit);
    setDisableIncrement(betInputAmount >= betMaxLimit || betInputAmount >= remainingBalance);
  }, [betInputAmount, betMaxLimit, betMinLimit, totalBet, isBetConfirmed, remainingBalance, betCode, dispatch]);

  useEffect(() => {
    // Adjusting the font size manually for extended text within the input field.
    if (inputRef.current) {
      const currFontSize = parseInt(getComputedStyle(inputRef.current).getPropertyValue('font-size'));
      const fontFamily = `${currFontSize}px Poppins`;
      const maxContentWidth = getTextWidth(betMaxLimit.toFixed(2), fontFamily);
      const availableWidth = inputRef.current.clientWidth - 20;
      if (maxContentWidth > availableWidth) {
        setCustomInputFontSize(() => {
          return Math.floor((Math.floor((availableWidth / maxContentWidth) * 100) / 100) * currFontSize);
        });
      }
    }
  }, [betMaxLimit]);

  useEffect(() => {
    if (quickBetClick[betCode].click && !forceTollTip) {
      if (betMaxLimitReached || balanceInSufficientReached) {
        setForceTollTip(true);
        setTooltipText(betMaxLimitReached ? 'CORE_MAXBETIS' : 'SHOW_NOT_ENOUGH_MONEY');
      }
      removeToaster();
    }
  }, [betMaxLimitReached, balanceInSufficientReached, quickBetClick]);

  useEffect(() => {
    if (forceTollTip) {
      const timer = setTimeout(() =>{ 
        setForceTollTip(false);
        setTooltipText(defaultToolTipText);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [forceTollTip]);

  if (showCollapsedMode) {
    return (
      <div className={styles.collapsed_bet_stepper}>
        <L10nText tk="BETLABEL" />:{' '}
        {CurrencyFormatter.format(betInputAmount, currentLanguage, { currency: currency, hideCurrency })}
      </div>
    );
  }

  return (
    <BetStepper
      prefixIcon={prefixComponent}
      postfixIcon={postFixComponent}
      betFieldValue={betInputValue}
      inputLabel={
        <>
          <L10nText tk={inputLabel} /> {currencySymbol}
        </>
      }
      inputRef={inputRef}
      tooltipText={tooltipText}
      onStepperDecrement={onStepperDecrement}
      onStepperIncrement={onStepperIncrement}
      onChange={handleOnChange}
      isEditing={isEditing}
      isDisabled={disabled || remainingBalance < betMinLimit}
      isDecrementDisabled={disableDecrement || isEditing || disabled}
      isIncrementDisabled={disableIncrement || isEditing || disabled}
      customInputFontSize={customInputFontSize}
      forceTriggerTooltip={forceTollTip}
      attrs={tooltipAttrs}
    />
  );
};
